import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Zap, Copy, RefreshCw, Check, Trash2, Clock, ChevronDown, ChevronRight,
  FileText, Mail, Target, BarChart3, Share2, Code2, Globe, Loader2, History,
  Sparkles, ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const CONTEXT_TYPES = [
  { value: 'general', label: 'General', icon: Globe },
  { value: 'meeting', label: 'Meeting / Minutes', icon: FileText },
  { value: 'email', label: 'Email / Communication', icon: Mail },
  { value: 'task', label: 'Task / Action Item', icon: Target },
  { value: 'report', label: 'Report / Analysis', icon: BarChart3 },
  { value: 'social_media', label: 'Social Media Post', icon: Share2 },
  { value: 'technical', label: 'Technical / Code', icon: Code2 },
];

const TONES = [
  { value: 'professional', label: 'Professional', emoji: '' },
  { value: 'friendly', label: 'Friendly', emoji: '' },
  { value: 'direct', label: 'Direct', emoji: '' },
  { value: 'formal', label: 'Formal', emoji: '' },
];

// ─── JSON Syntax Highlight ───
function JsonBlock({ data }) {
  const jsonStr = JSON.stringify(data, null, 2);
  const highlighted = jsonStr
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"([^"]+)":/g, '<span style="color:#7dd3fc">"$1"</span>:')
    .replace(/: "([^"]*)"/g, ': <span style="color:#86efac">"$1"</span>')
    .replace(/: (\d+)/g, ': <span style="color:#fcd34d">$1</span>')
    .replace(/: (true|false)/g, ': <span style="color:#c4b5fd">$1</span>');

  return (
    <pre
      className="text-xs leading-relaxed overflow-x-auto whitespace-pre-wrap break-words"
      style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}
      dangerouslySetInnerHTML={{ __html: highlighted }}
    />
  );
}

// ─── Shimmer Loader ───
function ShimmerLoader() {
  return (
    <div className="space-y-3 animate-pulse" data-testid="powerup-loading">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="flex gap-2 items-center">
          <div className="h-3 rounded bg-slate-700/60" style={{ width: `${40 + Math.random() * 55}%` }} />
          {i % 3 === 0 && <div className="h-3 rounded bg-cyan-900/30" style={{ width: `${15 + Math.random() * 25}%` }} />}
        </div>
      ))}
      <div className="flex items-center gap-2 mt-4 text-cyan-400/60 text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>AI is engineering your prompt...</span>
      </div>
    </div>
  );
}

// ─── Plain Text Formatter ───
function promptToPlainText(data) {
  if (!data) return '';
  const lines = [];
  if (data.role) lines.push(`ROLE: ${data.role}`);
  if (data.context) lines.push(`\nCONTEXT: ${data.context}`);
  if (data.task) lines.push(`\nTASK: ${data.task}`);
  if (data.instructions?.length) {
    lines.push('\nINSTRUCTIONS:');
    data.instructions.forEach((s, i) => lines.push(`  ${i + 1}. ${s}`));
  }
  if (data.constraints?.length) {
    lines.push('\nCONSTRAINTS:');
    data.constraints.forEach(c => lines.push(`  - ${c}`));
  }
  if (data.output_format) {
    const f = data.output_format;
    lines.push(`\nOUTPUT FORMAT: ${f.type || ''} | Tone: ${f.tone || ''} | Length: ${f.length || ''}`);
  }
  if (data.priority) lines.push(`\nPRIORITY: ${data.priority}`);
  return lines.join('\n');
}


export default function PromptPowerUpPage() {
  const { user } = useAuth();
  const [inputText, setInputText] = useState('');
  const [contextType, setContextType] = useState('general');
  const [tone, setTone] = useState('professional');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState('');
  const [activeTab, setActiveTab] = useState('powerup');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [selectionBtn, setSelectionBtn] = useState(null);
  const textareaRef = useRef(null);

  // ─── Selection detection for floating button ───
  const handleSelect = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const sel = ta.value.substring(ta.selectionStart, ta.selectionEnd).trim();
    if (sel.length > 5) {
      const rect = ta.getBoundingClientRect();
      // Estimate cursor position
      const lines = ta.value.substring(0, ta.selectionStart).split('\n');
      const lineHeight = 22;
      const topOffset = Math.min(lines.length * lineHeight, rect.height - 40);
      setSelectionBtn({ text: sel, top: topOffset });
    } else {
      setSelectionBtn(null);
    }
  }, []);

  // ─── PowerUp API call ───
  const handlePowerUp = async (text) => {
    if (!text || text.trim().length < 3) {
      toast.error('Please enter at least 3 characters');
      return;
    }
    setLoading(true);
    setResult(null);
    setSelectionBtn(null);
    try {
      const res = await axios.post(`${API}/api/prompt/powerup`, {
        selected_text: text.trim(),
        context_type: contextType,
        output_language: 'English',
        tone,
      });

      if (res.data.success) {
        setResult(res.data);
        toast.success('Prompt enhanced!');
      } else {
        toast.error(res.data.error || 'Enhancement failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enhance prompt');
    }
    setLoading(false);
  };

  // ─── Copy helpers ───
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(''), 2000);
  };

  // ─── History ───
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/api/prompt/history`);
      setHistory(res.data.history || []);
    } catch { /* */ }
    setHistoryLoading(false);
  };

  const deleteHistory = async (id) => {
    try {
      await axios.delete(`${API}/api/prompt/history/${id}`);
      setHistory(prev => prev.filter(h => h.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  useEffect(() => {
    if (activeTab === 'history') fetchHistory();
  }, [activeTab]);

  return (
    <div className="min-h-[calc(100vh-64px)] -m-4 md:-m-6 lg:-m-8 bg-[#0a0e1a]" data-testid="prompt-powerup-page">
      {/* Header */}
      <div className="border-b border-slate-800/60 px-4 py-4 md:px-8">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-yellow-400 flex items-center justify-center">
              <Zap className="h-5 w-5 text-slate-900" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white tracking-tight">Prompt PowerUp<PageInfoTooltip pageKey="prompt-powerup" /></h1>
              <p className="text-xs text-slate-500">AI prompt engineering on demand</p>
            </div>
          </div>

          <div className="flex gap-1 bg-slate-800/60 rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab('powerup')}
              className={cn("px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                activeTab === 'powerup' ? "bg-cyan-500/20 text-cyan-300" : "text-slate-500 hover:text-slate-300"
              )}
              data-testid="tab-powerup"
            >
              <Zap className="h-3 w-3 inline mr-1" />PowerUp
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={cn("px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                activeTab === 'history' ? "bg-cyan-500/20 text-cyan-300" : "text-slate-500 hover:text-slate-300"
              )}
              data-testid="tab-history"
            >
              <History className="h-3 w-3 inline mr-1" />History
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'powerup' ? (
        <div className="max-w-7xl mx-auto px-4 py-6 md:px-8">
          {/* Controls bar */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            {/* Context */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Context:</span>
              {CONTEXT_TYPES.map(ct => {
                const Icon = ct.icon;
                return (
                  <button
                    key={ct.value}
                    onClick={() => setContextType(ct.value)}
                    className={cn("flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border",
                      contextType === ct.value
                        ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/30"
                        : "bg-slate-800/40 text-slate-500 border-slate-700/30 hover:text-slate-300"
                    )}
                    data-testid={`context-${ct.value}`}
                  >
                    <Icon className="h-3 w-3" />{ct.label}
                  </button>
                );
              })}
            </div>

            <div className="w-px h-5 bg-slate-700/50 hidden md:block" />

            {/* Tone */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Tone:</span>
              {TONES.map(t => (
                <button
                  key={t.value}
                  onClick={() => setTone(t.value)}
                  className={cn("px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border",
                    tone === t.value
                      ? "bg-yellow-500/15 text-yellow-300 border-yellow-500/30"
                      : "bg-slate-800/40 text-slate-500 border-slate-700/30 hover:text-slate-300"
                  )}
                  data-testid={`tone-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Split Panel */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* LEFT: Input */}
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Your Rough Prompt</span>
              </div>
              <Card className="bg-[#0d1117] border-slate-700/40 flex-1 flex flex-col relative">
                <CardContent className="p-0 flex-1 flex flex-col relative">
                  <textarea
                    ref={textareaRef}
                    value={inputText}
                    onChange={e => setInputText(e.target.value)}
                    onSelect={handleSelect}
                    onBlur={() => setTimeout(() => setSelectionBtn(null), 200)}
                    placeholder="Type or paste your rough idea, phrase, or prompt here... then highlight it and click the PowerUp button"
                    className="w-full flex-1 min-h-[340px] bg-transparent text-slate-200 text-sm p-4 resize-none focus:outline-none placeholder:text-slate-600/70"
                    style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace", lineHeight: '22px' }}
                    data-testid="powerup-input"
                  />

                  {/* Floating selection PowerUp button */}
                  {selectionBtn && (
                    <button
                      onMouseDown={(e) => { e.preventDefault(); handlePowerUp(selectionBtn.text); }}
                      className="absolute left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-900 bg-gradient-to-r from-cyan-400 to-yellow-300 shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] transition-all"
                      style={{ top: `${selectionBtn.top - 8}px` }}
                      data-testid="selection-powerup-btn"
                    >
                      <Zap className="h-3.5 w-3.5" />POWERUP SELECTION
                    </button>
                  )}
                </CardContent>

                {/* Full prompt button */}
                <div className="p-3 pt-0">
                  <Button
                    onClick={() => handlePowerUp(inputText)}
                    disabled={loading || inputText.trim().length < 3}
                    className="w-full h-11 bg-gradient-to-r from-cyan-500 to-yellow-400 hover:from-cyan-400 hover:to-yellow-300 text-slate-900 font-bold text-sm gap-2 rounded-lg shadow-[0_0_20px_rgba(6,182,212,0.25)] disabled:opacity-40"
                    data-testid="powerup-full-btn"
                  >
                    {loading ? (
                      <><Loader2 className="h-4 w-4 animate-spin" />Enhancing...</>
                    ) : (
                      <><Zap className="h-4 w-4" />POWERUP FULL PROMPT</>
                    )}
                  </Button>
                </div>
              </Card>
            </div>

            {/* RIGHT: Output */}
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Your Professional JSON Prompt</span>
                {result?.tokens_used > 0 && (
                  <Badge className="bg-cyan-500/15 text-cyan-400 border-cyan-500/30 text-[10px] gap-1">
                    <Zap className="h-2.5 w-2.5" />{result.tokens_used} tokens
                  </Badge>
                )}
              </div>
              <Card className="bg-[#0d1117] border-slate-700/40 flex-1 flex flex-col">
                <CardContent className="p-4 flex-1 flex flex-col">
                  {loading ? (
                    <ShimmerLoader />
                  ) : result?.enhanced_prompt ? (
                    <div className="flex-1 flex flex-col">
                      <div className="flex-1 overflow-y-auto max-h-[320px] mb-3">
                        <JsonBlock data={result.enhanced_prompt} />
                      </div>

                      {/* Action buttons */}
                      <div className="flex flex-wrap gap-2 mt-auto pt-3 border-t border-slate-800/60">
                        <Button
                          size="sm" variant="outline"
                          className="border-slate-700 text-slate-300 text-xs gap-1.5 hover:bg-slate-800"
                          onClick={() => copyToClipboard(JSON.stringify(result.enhanced_prompt, null, 2), 'JSON')}
                          data-testid="copy-json-btn"
                        >
                          {copied === 'JSON' ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                          Copy JSON
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="border-slate-700 text-slate-300 text-xs gap-1.5 hover:bg-slate-800"
                          onClick={() => copyToClipboard(promptToPlainText(result.enhanced_prompt), 'Plain')}
                          data-testid="copy-plain-btn"
                        >
                          {copied === 'Plain' ? <Check className="h-3 w-3 text-emerald-400" /> : <FileText className="h-3 w-3" />}
                          Copy as Plain Text
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="border-slate-700 text-slate-300 text-xs gap-1.5 hover:bg-slate-800"
                          onClick={() => handlePowerUp(result.original_text || inputText)}
                          data-testid="regenerate-btn"
                        >
                          <RefreshCw className="h-3 w-3" />Re-generate
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-500 text-xs gap-1.5"
                          onClick={() => {
                            copyToClipboard(JSON.stringify(result.enhanced_prompt, null, 2), 'Use');
                            toast.success('Prompt copied! Paste it into any AI tool.');
                          }}
                          data-testid="use-prompt-btn"
                        >
                          <Check className="h-3 w-3" />Use This Prompt
                        </Button>
                      </div>

                      {/* Plain summary */}
                      {result.plain_summary && (
                        <div className="mt-3 p-3 bg-slate-800/40 rounded-lg border border-slate-700/30">
                          <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 font-medium">What this prompt does</p>
                          <p className="text-xs text-slate-400 leading-relaxed">{result.plain_summary}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-center">
                      <div>
                        <div className="w-14 h-14 rounded-2xl bg-slate-800/60 flex items-center justify-center mx-auto mb-3">
                          <Sparkles className="h-6 w-6 text-slate-600" />
                        </div>
                        <p className="text-sm text-slate-500 mb-1">No prompt generated yet</p>
                        <p className="text-xs text-slate-600">Type something on the left and hit PowerUp</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Prompt Battle: Before → After */}
          {result?.enhanced_prompt && result?.original_text && (
            <div className="mt-6" data-testid="prompt-battle">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Before / After</span>
                <Badge className="bg-yellow-500/15 text-yellow-400 border-yellow-500/30 text-[10px]">Prompt Battle</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
                {/* Before */}
                <Card className="bg-red-500/5 border-red-500/20">
                  <CardContent className="p-4">
                    <p className="text-[10px] uppercase tracking-wider text-red-400/70 mb-2 font-medium">Before</p>
                    <p className="text-sm text-slate-400 leading-relaxed italic">"{result.original_text}"</p>
                  </CardContent>
                </Card>

                <div className="hidden md:flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-yellow-400 flex items-center justify-center">
                    <ArrowRight className="h-5 w-5 text-slate-900" />
                  </div>
                </div>

                {/* After */}
                <Card className="bg-emerald-500/5 border-emerald-500/20">
                  <CardContent className="p-4">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-400/70 mb-2 font-medium">After (PowerUp)</p>
                    <p className="text-sm text-white font-medium mb-1">{result.enhanced_prompt.role}</p>
                    <p className="text-xs text-slate-400 mb-2">{result.enhanced_prompt.task}</p>
                    {result.enhanced_prompt.instructions?.slice(0, 3).map((inst, i) => (
                      <p key={i} className="text-xs text-emerald-300/70 flex gap-1.5">
                        <span className="text-emerald-500">{i + 1}.</span>{inst}
                      </p>
                    ))}
                    {(result.enhanced_prompt.instructions?.length || 0) > 3 && (
                      <p className="text-[10px] text-slate-600 mt-1">+{result.enhanced_prompt.instructions.length - 3} more instructions</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Share */}
              <div className="flex justify-center mt-3">
                <Button
                  size="sm" variant="outline"
                  className="border-slate-700 text-slate-400 text-xs gap-1.5"
                  onClick={() => {
                    const shareText = `Before:\n"${result.original_text}"\n\nAfter (PowerUp):\n${JSON.stringify(result.enhanced_prompt, null, 2)}`;
                    navigator.clipboard.writeText(shareText);
                    toast.success('Before/After copied! Share it anywhere.');
                  }}
                  data-testid="share-battle-btn"
                >
                  <Share2 className="h-3 w-3" />Share this PowerUp
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ─── History Tab ─── */
        <div className="max-w-4xl mx-auto px-4 py-6 md:px-8" data-testid="history-tab">
          {historyLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 text-cyan-400 animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-20">
              <History className="h-10 w-10 text-slate-700 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No PowerUp history yet</p>
              <p className="text-xs text-slate-600 mt-1">Your enhanced prompts will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map(item => (
                <Card
                  key={item.id}
                  className={cn("bg-slate-800/40 border-slate-700/40 transition-all cursor-pointer hover:border-slate-600/60",
                    expandedHistory === item.id && "border-cyan-500/30"
                  )}
                  onClick={() => setExpandedHistory(expandedHistory === item.id ? null : item.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {expandedHistory === item.id ? <ChevronDown className="h-3.5 w-3.5 text-cyan-400 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
                          <p className="text-sm text-white truncate">{item.original_text}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-5">
                          <Badge className="bg-slate-700/60 text-slate-400 text-[10px]">{item.context_type}</Badge>
                          <span className="text-[10px] text-slate-600 flex items-center gap-1">
                            <Clock className="h-2.5 w-2.5" />
                            {new Date(item.created_at).toLocaleDateString()} {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 w-7 p-0 text-slate-500 hover:text-cyan-400"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(JSON.stringify(item.enhanced_prompt, null, 2), 'History');
                          }}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 w-7 p-0 text-slate-500 hover:text-red-400"
                          onClick={(e) => { e.stopPropagation(); deleteHistory(item.id); }}
                          data-testid={`delete-history-${item.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    {expandedHistory === item.id && item.enhanced_prompt && (
                      <div className="mt-3 pt-3 border-t border-slate-700/40 ml-5">
                        <div className="bg-[#0d1117] rounded-lg p-3 max-h-64 overflow-y-auto">
                          <JsonBlock data={item.enhanced_prompt} />
                        </div>
                        {item.plain_summary && (
                          <p className="text-xs text-slate-500 mt-2 italic">{item.plain_summary}</p>
                        )}
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" variant="outline" className="border-slate-700 text-slate-400 text-[10px] h-7 gap-1"
                            onClick={(e) => { e.stopPropagation(); setInputText(item.original_text); setActiveTab('powerup'); }}
                          >
                            <RefreshCw className="h-2.5 w-2.5" />Re-PowerUp
                          </Button>
                          <Button size="sm" className="bg-emerald-600 text-[10px] h-7 gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(JSON.stringify(item.enhanced_prompt, null, 2), 'Use');
                            }}
                          >
                            <Check className="h-2.5 w-2.5" />Use This
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
